import { Alert, Button, FormControl, LinearProgress, Link, Paper, TextField, Typography, Divider, Box } from "@mui/material";
import { withStyles } from "@mui/styles";
import React, { FormEvent, useEffect, useState } from "react";
import styles from "./LoginPage-jss";
import { Grid } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { live4tvUserService } from "./live4tvUserService";
import { connect } from "react-redux";
import { Auth, Profile } from "./userDTO";
import useCrashReport from "packages/useCrashReport/useCrashReport";
import { useNavigate } from "react-router-dom";
import { setIsLoggedIn, setUserProfile } from "./userActions";
import useGTM from "../../packages/useGTM/useGTM";
import GoogleSignInButton from "./GoogleSignInButton";
import FacebookSignInButton from "./FacebookSignInButton";

type Properties = {
    classes: any,
    auth: Auth,
    profile: Profile,
    setUserProfile: Function,
    setIsLoggedIn: Function,
}

function MagicLinkLoginPage(props: Properties) {
    const {
        classes,
        auth,
        profile,
        setUserProfile,
        setIsLoggedIn,
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [isFacebookLoading, setIsFacebookLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [success, setSuccess] = useState('');

    const [email, setEmail] = useState('');

    const { t } = useTranslation();

    const crashReporter = useCrashReport();
    const gtm = useGTM({});

    const navigate = useNavigate();

    useEffect(() => {
        console.debug('signedIn', auth.isLoggedIn);
        if (auth.isLoggedIn === true) {
            crashReporter.setUser(profile.code);
            navigate('/events');
        }
    }, [auth.isLoggedIn]);

    async function generateMagicLink(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        setIsLoading(true);
        setFormError('');

        try {
            const response = await live4tvUserService.generateMagicLink(email);
            console.debug('generateMagicLink().response', response);
            setSuccess(t('Login link generated successfully. Please check your email for the link. It may take a few minutes to arrive.'));

        } catch (error: any) {
            console.debug('generateMagicLink().error', error);
            if (error.status !== undefined && error.status !== "400") {
                console.error('generateMagicLink().error', error);
            }

            setFormError(t('Invalid email or password'));
        }

        setIsLoading(false);
    }

    async function handleGoogleSignIn(credential: string) {
        setIsGoogleLoading(true);
        setFormError('');
        setSuccess('');

        try {
            const response = await live4tvUserService.loginWithGoogle(credential);
            console.debug('handleGoogleSignIn().login', response);

            const user = await live4tvUserService.getUser(response.token);
            console.log('handleGoogleSignIn().user', user);

            const profile: Profile = {
                firstname: user.firstname,
                lastnames: user.lastnames,
                code: user.code,
                email: user.email,
                username: user.username,
                token: response.token,
                roles: response.roles,
            }

            setUserProfile(profile);
            setIsLoggedIn(true);
            crashReporter.setUser(profile.code);
            gtm.trackEvent('login', {
                method: 'google',
                user: {
                    id: profile.code
                },
            });
        } catch (error: any) {
            console.debug('handleGoogleSignIn().error', error);
            if (error.status !== undefined && error.status !== "400") {
                console.error('handleGoogleSignIn().error', error);
            }

            setFormError(t('Google authentication failed. Please try again.'));
        }

        setIsGoogleLoading(false);
    }

    function handleGoogleError(error: string) {
        setFormError(error);
    }

    async function handleFacebookSignIn(accessToken: string) {
        setIsFacebookLoading(true);
        setFormError('');
        setSuccess('');

        try {
            const response = await live4tvUserService.loginWithFacebook(accessToken);
            console.debug('handleFacebookSignIn().login', response);

            const user = await live4tvUserService.getUser(response.token);
            console.log('handleFacebookSignIn().user', user);

            const profile: Profile = {
                firstname: user.firstname,
                lastnames: user.lastnames,
                code: user.code,
                email: user.email,
                username: user.username,
                token: response.token,
                roles: response.roles,
            }

            setUserProfile(profile);
            setIsLoggedIn(true);
            crashReporter.setUser(profile.code);
            gtm.trackEvent('login', {
                method: 'facebook',
                user: {
                    id: profile.code
                },
            });
        } catch (error: any) {
            console.debug('handleFacebookSignIn().error', error);
            if (error.status !== undefined && error.status !== "400") {
                console.error('handleFacebookSignIn().error', error);
            }

            setFormError(t('Facebook authentication failed. Please try again.'));
        }

        setIsFacebookLoading(false);
    }

    function handleFacebookError(error: string) {
        setFormError(error);
    }

    return (
        <Grid container className={classNames(classes.root)} direction="row" alignItems="center" justifyContent="center">
            <Grid item xs={10} md={8} lg={6} container direction="column">
                <Paper className={classNames(classes.paperForm)} elevation={0} square>
                    {success ? <Alert severity="success" className={classNames(classes.alert)}>{success}</Alert> : (
                        <div className={classNames(classes.form)}>
                            {(isLoading || isGoogleLoading || isFacebookLoading) ? <LinearProgress className={classNames(classes.progressBar)}/> : (
                                <>
                                    <Typography component="h1" variant="h5">
                                        {t("Login with your email")}
                                    </Typography>

                                    {formError ? <Alert severity="error" className={classNames(classes.alert)}>{formError}</Alert> : ''}

                                    <form onSubmit={generateMagicLink}>
                                        <FormControl fullWidth className={classNames(classes.textField)}>
                                            <TextField
                                                autoFocus
                                                margin="dense"
                                                id="login"
                                                label={t("Enter with your Live4.tv email")}
                                                type="text"
                                                fullWidth
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                        </FormControl>
                                        <FormControl fullWidth className={classNames(classes.textField)}>
                                        </FormControl>

                                        <Button fullWidth size="large" type="submit" variant="contained" color="primary"
                                                className={classNames(classes.submit)} disabled={isLoading || isGoogleLoading || isFacebookLoading}>
                                            {t("Generate Login link")}
                                        </Button>
                                    </form>

                                    <Box sx={{ display: 'flex', alignItems: 'center', marginY: 2 }}>
                                        <Divider sx={{ flexGrow: 1 }} />
                                        <Typography variant="body2" sx={{ marginX: 2, color: 'text.secondary' }}>
                                            {t("OR")}
                                        </Typography>
                                        <Divider sx={{ flexGrow: 1 }} />
                                    </Box>

                                    <Box sx={{ marginY: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <GoogleSignInButton
                                            onSuccess={handleGoogleSignIn}
                                            onError={handleGoogleError}
                                            disabled={isLoading || isGoogleLoading || isFacebookLoading}
                                            text={t("Continue with Google")}
                                            className={classNames(classes.textField)}
                                        />
                                        <FacebookSignInButton
                                            onSuccess={handleFacebookSignIn}
                                            onError={handleFacebookError}
                                            disabled={isLoading || isGoogleLoading || isFacebookLoading}
                                            text={t("Continue with Facebook")}
                                            className={classNames(classes.textField)}
                                        />
                                    </Box>
                                </>
                            )}
                        </div>
                    )}

                </Paper>

                <Link href="#signup" color="textPrimary">
                    {t("You still have no account? Signup here!")}
                </Link>
            </Grid>
        </Grid>
    )
}

const mapStateToProps = function (state: any) {
    return {
        auth: state.auth,
        profile: state.user.profile
    }
}

const mapDispatchToProps = function (dispatch: any) {
    return {
        setIsLoggedIn: (signedIn: boolean) => dispatch(setIsLoggedIn(signedIn)),
        setUserProfile: (profile: Profile) => dispatch(setUserProfile(profile)),
    }
}

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(MagicLinkLoginPage));
